/**
 * Lane presentation for the repository Fixes board and its per-lane list views.
 *
 * The backend decides which lane a fix is in; everything here is about how a lane and
 * the fixes in it read on screen — the column heading, the one-line status under a
 * card's title, which timestamp is the meaningful one for that stage.
 */
import { FixLane, FixPullRequest, FixResponse } from '../../types/fix';

export interface FixLaneMeta {
  id: FixLane;
  /** Column heading on the board. */
  label: string;
  /** Copy for a lane with nothing in it. */
  empty: string;
}

/** Board columns, left to right. `needs_attention` joins them only when it has fixes. */
export const BOARD_LANES: FixLane[] = ['in_progress', 'ready', 'pr_open', 'completed'];

export const LANE_META: Record<FixLane, FixLaneMeta> = {
  in_progress: {
    id: 'in_progress',
    label: 'In progress',
    empty: 'No fixes are being written right now.',
  },
  ready: {
    id: 'ready',
    label: 'Ready',
    empty: 'No fixes are waiting for review.',
  },
  pr_open: {
    id: 'pr_open',
    label: 'PR in progress',
    empty: 'No pull requests are open.',
  },
  completed: {
    id: 'completed',
    label: 'Completed',
    empty: 'No fixes have merged yet.',
  },
  needs_attention: {
    id: 'needs_attention',
    label: 'Needs attention',
    empty: 'Nothing needs attention.',
  },
};

/** The merged PR of a fix, if one merged. */
export const mergedPullRequest = (fix: FixResponse): FixPullRequest | undefined =>
  fix.pull_requests?.find((pr) => pr.merge_status === 'merged');

/** The open PR of a fix. PRs created before merge tracking carry no status; treat them as open. */
export const openPullRequest = (fix: FixResponse): FixPullRequest | undefined =>
  fix.pull_requests?.find((pr) => !pr.merge_status || pr.merge_status === 'open');

/** The PR worth linking to for a fix in `lane`. */
export const lanePullRequest = (fix: FixResponse, lane: FixLane): FixPullRequest | undefined => {
  if (lane === 'completed') return mergedPullRequest(fix) || fix.pull_requests?.[0];
  if (lane === 'pr_open') return openPullRequest(fix) || fix.pull_requests?.[0];
  return fix.pull_requests?.[0];
};

/**
 * The timestamp that matters for a fix at this stage: when the agent picked it up, when
 * the patch became reviewable, when the PR opened, when it merged. Falls back to the
 * creation time, which every fix has.
 */
export const laneTimestamp = (fix: FixResponse, lane: FixLane): string => {
  switch (lane) {
    case 'in_progress':
      return fix.started_at || fix.created_at;
    case 'ready':
      return fix.last_refined_at || fix.last_regenerated_at || fix.completed_at || fix.created_at;
    case 'pr_open':
      return lanePullRequest(fix, lane)?.created_at || fix.completed_at || fix.created_at;
    case 'completed':
      return mergedPullRequest(fix)?.merged_at || lanePullRequest(fix, lane)?.created_at || fix.created_at;
    case 'needs_attention':
    default:
      return fix.completed_at || fix.cancelled_at || fix.created_at;
  }
};

/**
 * Short, glanceable age: "just now", "8 min", "3 hr", "yesterday", then a date.
 * `now` is injectable so this stays testable.
 */
export const relativeAge = (iso: string | undefined, now: number = Date.now()): string => {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';

  const minutes = Math.floor((now - then) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr`;
  if (hours < 48) return 'yesterday';

  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

/** "3 files" / "1 file", or null when the fix has no diff yet. */
export const filesChangedLabel = (fix: FixResponse): string | null => {
  const count = fix.changed_file_count;
  if (!count) return null;
  return `${count} ${count === 1 ? 'file' : 'files'}`;
};

export interface DiffStat {
  added: number;
  removed: number;
}

/**
 * The +/− line counts of a fix's patch, or null when there is no patch yet. A patch that
 * only adds or only removes still counts: zero is a real number here, absent is not.
 */
export const diffStat = (fix: FixResponse): DiffStat | null => {
  const { lines_added: added, lines_removed: removed } = fix;
  if (added === undefined && removed === undefined) return null;
  return { added: added ?? 0, removed: removed ?? 0 };
};

/** What the agent is doing right now, for a fix in the in_progress lane. */
export const inProgressActivity = (fix: FixResponse): string => {
  if (fix.refinement_pending) return 'agent refining';
  if (fix.regeneration_pending) return 'agent regenerating';
  if (fix.status === 'pending') return 'queued';
  return 'agent investigating';
};

const ATTENTION_REASON: Record<string, string> = {
  failed: 'fix failed',
  cancelled: 'fix stopped',
  pr_creation_failed: 'PR failed',
  pr_update_failed: 'PR update failed',
};

/** Why a fix landed in the needs_attention lane. */
export const attentionReason = (fix: FixResponse): string =>
  ATTENTION_REASON[fix.status] || 'PR closed unmerged';

/**
 * The line under a card's title: the lane's timestamp plus what it means at this stage
 * ("8 min · 3 files", "PR #142 · Aug 11", "PR #131 merged · Aug 9").
 */
export const fixCardMeta = (fix: FixResponse, lane: FixLane, now: number = Date.now()): string => {
  const age = relativeAge(laneTimestamp(fix, lane), now);

  if (lane === 'in_progress') return `${age} · ${inProgressActivity(fix)}`;

  if (lane === 'ready') {
    if (fix.pr_creation_pending) return `${age} · opening PR…`;
    const files = filesChangedLabel(fix);
    return files ? `${age} · ${files}` : age;
  }

  if (lane === 'pr_open') {
    const pr = lanePullRequest(fix, lane);
    return pr ? `PR #${pr.pr_number} · ${age}` : age;
  }

  if (lane === 'completed') {
    const pr = lanePullRequest(fix, lane);
    return pr ? `PR #${pr.pr_number} merged · ${age}` : `merged · ${age}`;
  }

  return `${attentionReason(fix)} · ${age}`;
};
